// ============================================
// Event Invites Management
// ============================================

import React from 'react';
import { collection, addDoc, onSnapshot, deleteDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { Event, EventInvite } from '../../../types';
import { v4 as uuidv4 } from 'uuid';

interface Props {
  event: Event;
}

// Extended invite type with email allowlist
interface ExtendedInvite extends EventInvite {
  allowedEmails?: string[];
}

export default function EventInvites({ event }: Props) {
  const { user } = useAuth();
  const [invites, setInvites] = React.useState<ExtendedInvite[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [creating, setCreating] = React.useState(false);

  // Form state
  const [role, setRole] = React.useState<'therapist' | 'admin'>('therapist');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [maxUses, _setMaxUses] = React.useState(50);
  const [emailInput, setEmailInput] = React.useState('');
  const [allowedEmails, setAllowedEmails] = React.useState<string[]>([]);

  // QR Code state
  const [qrDataUrl, setQrDataUrl] = React.useState<string | null>(null);
  const [generatingQr, setGeneratingQr] = React.useState(false);

  // Edit mode state
  const [editingInviteId, setEditingInviteId] = React.useState<string | null>(null);
  const [editEmailInput, setEditEmailInput] = React.useState('');
  const [editEmails, setEditEmails] = React.useState<string[]>([]);

  React.useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'events', event.id, 'invites'),
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as ExtendedInvite[];
        setInvites(data);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [event.id]);

  const generateCode = () => {
    return uuidv4().substring(0, 8).toUpperCase();
  };

  const addEmail = () => {
    const email = emailInput.trim().toLowerCase();
    if (!email) return;

    // Basic email validation
    if (!email.includes('@') || !email.includes('.')) {
      alert('Please enter a valid email address');
      return;
    }

    // Check max emails for admin (5) or therapist (unlimited but reasonable)
    const maxEmails = role === 'admin' ? 5 : 50;
    if (allowedEmails.length >= maxEmails) {
      alert(`Maximum ${maxEmails} email addresses allowed for ${role} invites`);
      return;
    }

    if (allowedEmails.includes(email)) {
      alert('Email already added');
      return;
    }

    setAllowedEmails([...allowedEmails, email]);
    setEmailInput('');
  };

  const removeEmail = (email: string) => {
    setAllowedEmails(allowedEmails.filter((e) => e !== email));
  };

  const createInvite = async () => {
    if (allowedEmails.length === 0) {
      alert('Please add at least one email address');
      return;
    }

    setCreating(true);
    try {
      const code = generateCode();
      await addDoc(collection(db, 'events', event.id, 'invites'), {
        code,
        role,
        maxUses: allowedEmails.length, // Max uses equals number of emails
        uses: 0,
        allowedEmails,
        createdAt: serverTimestamp(),
        createdBy: user?.uid,
      });
      // Reset form
      setAllowedEmails([]);
      setEmailInput('');
      alert(`Invite created! Share the URL with your ${role === 'admin' ? 'admins' : 'therapists'}.`);
    } catch (err: any) {
      console.error('Error creating invite:', err);
      alert('Error creating invite: ' + (err.message || 'Unknown error'));
    } finally {
      setCreating(false);
    }
  };

  const deleteInvite = async (inviteId: string) => {
    if (!window.confirm('Delete this invite code?')) return;
    await deleteDoc(doc(db, 'events', event.id, 'invites', inviteId));
  };

  // Start editing an invite
  const startEditInvite = (invite: ExtendedInvite) => {
    setEditingInviteId(invite.id);
    setEditEmails(invite.allowedEmails || []);
    setEditEmailInput('');
  };

  // Cancel editing
  const cancelEditInvite = () => {
    setEditingInviteId(null);
    setEditEmails([]);
    setEditEmailInput('');
  };

  // Add email to edit list
  const addEditEmail = () => {
    const email = editEmailInput.trim().toLowerCase();
    if (!email) return;

    if (!email.includes('@') || !email.includes('.')) {
      alert('Please enter a valid email address');
      return;
    }

    if (editEmails.includes(email)) {
      alert('Email already in the list');
      return;
    }

    setEditEmails([...editEmails, email]);
    setEditEmailInput('');
  };

  // Remove email from edit list
  const removeEditEmail = (email: string) => {
    setEditEmails(editEmails.filter((e) => e !== email));
  };

  // Save edited invite
  const saveEditInvite = async () => {
    if (!editingInviteId || editEmails.length === 0) return;

    try {
      await updateDoc(doc(db, 'events', event.id, 'invites', editingInviteId), {
        allowedEmails: editEmails,
        maxUses: editEmails.length,
      });
      cancelEditInvite();
      alert('Invite updated successfully!');
    } catch (err: any) {
      console.error('Error updating invite:', err);
      alert('Error updating invite: ' + (err.message || 'Unknown error'));
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const getJoinUrl = (code: string, inviteRole: string) => {
    const basePath = inviteRole === 'admin' ? '/admin/join' : '/t/join';
    return `${window.location.origin}${basePath}?code=${code}&event=${event.id}`;
  };

  const getPlayerUrl = () => {
    return `${window.location.origin}/e/${event.id}`;
  };

  // Generate QR Code using a free API
  const generateQrCode = async () => {
    setGeneratingQr(true);
    try {
      const playerUrl = getPlayerUrl();
      // Use QR Server API (free, no API key needed)
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(playerUrl)}&bgcolor=0f172a&color=34d399&format=png`;

      // Fetch the QR code and convert to data URL for download
      const response = await fetch(qrUrl);
      const blob = await response.blob();
      const dataUrl = URL.createObjectURL(blob);
      setQrDataUrl(dataUrl);
    } catch (err) {
      console.error('Error generating QR code:', err);
      alert('Failed to generate QR code');
    } finally {
      setGeneratingQr(false);
    }
  };

  const downloadQrCode = () => {
    if (!qrDataUrl) return;

    const link = document.createElement('a');
    link.href = qrDataUrl;
    link.download = `${event.name.replace(/[^a-z0-9]/gi, '-')}-qr-code.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return <div className="loading">Loading invites...</div>;
  }

  return (
    <div className="event-invites">
      <div className="section-header">
        <h2>Invite Codes</h2>
      </div>

      {/* Signup URLs Info Box */}
      <div style={{
        padding: '1.5rem',
        background: 'var(--color-bg-secondary)',
        borderRadius: '1rem',
        marginBottom: '2rem',
        border: '1px solid var(--color-border)'
      }}>
        <h3 style={{ marginBottom: '1rem', color: 'var(--color-primary-light)' }}>How Invites Work</h3>
        <ol style={{ marginLeft: '1.25rem', marginBottom: '1rem', lineHeight: '1.8' }}>
          <li>Add email addresses below and create an invite code</li>
          <li>Share the invite URL with the person</li>
          <li>They sign in with Google (using the invited email)</li>
          <li>They enter the invite code to join</li>
        </ol>
        <div style={{ display: 'grid', gap: '1rem' }}>
          <div>
            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>
              Admin Signup URL:
            </div>
            <code style={{ fontSize: '0.8rem', padding: '0.5rem', background: 'var(--color-bg)', borderRadius: '4px', display: 'block', wordBreak: 'break-all' }}>
              {window.location.origin}/admin/join?event={event.id}
            </code>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>
              Therapist Signup URL:
            </div>
            <code style={{ fontSize: '0.8rem', padding: '0.5rem', background: 'var(--color-bg)', borderRadius: '4px', display: 'block', wordBreak: 'break-all' }}>
              {window.location.origin}/t/join?event={event.id}
            </code>
          </div>
        </div>
      </div>

      {/* Create Invite Form */}
      <div className="create-invite-form">
        <h3>Create New Invite</h3>

        {/* Role Selection - Large Buttons */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ marginBottom: '0.75rem', display: 'block', color: 'var(--color-text-secondary)', fontWeight: 600 }}>
            Select Role
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <button
              type="button"
              onClick={() => { setRole('therapist'); setAllowedEmails([]); }}
              className={`btn btn-large ${role === 'therapist' ? 'btn-primary' : 'btn-secondary'}`}
              style={{ flexDirection: 'column', padding: '1.5rem', height: 'auto' }}
            >
              <span style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>💆</span>
              <span>Therapist</span>
            </button>
            <button
              type="button"
              onClick={() => { setRole('admin'); setAllowedEmails([]); }}
              className={`btn btn-large ${role === 'admin' ? 'btn-primary' : 'btn-secondary'}`}
              style={{ flexDirection: 'column', padding: '1.5rem', height: 'auto' }}
            >
              <span style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>👔</span>
              <span>Event Admin</span>
            </button>
          </div>
        </div>

        {/* Email Allowlist */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ marginBottom: '0.5rem', display: 'block', color: 'var(--color-text-secondary)', fontWeight: 600 }}>
            Allowed Email Addresses {role === 'admin' && '(max 5)'}
          </label>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: '0.75rem' }}>
            Only these email addresses can use the invite code
          </p>

          {/* Email input */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
            <input
              type="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addEmail())}
              placeholder="Enter email address"
              style={{ flex: 1 }}
            />
            <button
              type="button"
              onClick={addEmail}
              className="btn btn-secondary"
              disabled={role === 'admin' && allowedEmails.length >= 5}
            >
              + Add
            </button>
          </div>

          {/* Email list */}
          {allowedEmails.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {allowedEmails.map((email) => (
                <div
                  key={email}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    background: 'var(--color-bg-tertiary)',
                    borderRadius: '9999px',
                    fontSize: '0.875rem',
                  }}
                >
                  <span>{email}</span>
                  <button
                    type="button"
                    onClick={() => removeEmail(email)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--color-red)',
                      cursor: 'pointer',
                      padding: 0,
                      fontSize: '1rem',
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <button
          onClick={createInvite}
          disabled={creating || allowedEmails.length === 0}
          className="btn btn-large btn-success"
          style={{ width: '100%' }}
        >
          {creating ? 'Creating...' : `Create ${role === 'admin' ? 'Admin' : 'Therapist'} Invite (${allowedEmails.length} ${allowedEmails.length === 1 ? 'person' : 'people'})`}
        </button>
      </div>

      {/* Existing Invites */}
      <div style={{ marginTop: '2rem' }}>
        <h3>Active Invites</h3>
        {invites.length === 0 ? (
          <div className="empty-state">
            <p>No invite codes yet. Create one above.</p>
          </div>
        ) : (
          <div className="invites-list">
            {invites.map((invite) => (
              <div key={invite.id} className="invite-card" style={{ flexDirection: 'column', gap: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <div className="invite-code">
                    <span className="code">{invite.code}</span>
                    <span className={`badge ${invite.role}`}>{invite.role}</span>
                  </div>
                  <div className="invite-meta">
                    <span className="uses">
                      {invite.uses} / {invite.maxUses} uses
                    </span>
                  </div>
                </div>

                {/* Allowed emails - Edit Mode */}
                {editingInviteId === invite.id ? (
                  <div style={{ width: '100%', padding: '1rem', background: 'var(--color-bg)', borderRadius: '0.5rem' }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem', color: 'var(--color-primary-light)' }}>
                      Edit Allowed Emails
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <input
                        type="email"
                        value={editEmailInput}
                        onChange={(e) => setEditEmailInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addEditEmail())}
                        placeholder="Add email address"
                        style={{ flex: 1 }}
                      />
                      <button type="button" onClick={addEditEmail} className="btn btn-sm btn-secondary">
                        + Add
                      </button>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
                      {editEmails.map((email) => (
                        <div
                          key={email}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.25rem 0.5rem',
                            background: 'var(--color-bg-tertiary)',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                          }}
                        >
                          <span>{email}</span>
                          <button
                            type="button"
                            onClick={() => removeEditEmail(email)}
                            style={{ background: 'none', border: 'none', color: 'var(--color-red)', cursor: 'pointer', padding: 0 }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button onClick={saveEditInvite} className="btn btn-sm btn-success" disabled={editEmails.length === 0}>
                        Save Changes
                      </button>
                      <button onClick={cancelEditInvite} className="btn btn-sm btn-secondary">
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Allowed emails - View Mode */
                  invite.allowedEmails && invite.allowedEmails.length > 0 && (
                    <div style={{ width: '100%' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginBottom: '0.5rem' }}>
                        Allowed emails:
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                        {invite.allowedEmails.map((email) => (
                          <span
                            key={email}
                            style={{
                              fontSize: '0.75rem',
                              padding: '0.25rem 0.5rem',
                              background: 'var(--color-bg-tertiary)',
                              borderRadius: '4px',
                            }}
                          >
                            {email}
                          </span>
                        ))}
                      </div>
                    </div>
                  )
                )}

                {/* Invite URL */}
                <div style={{ width: '100%', padding: '0.75rem', background: 'var(--color-bg)', borderRadius: '0.5rem' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>
                    Invite URL:
                  </div>
                  <code style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                    {getJoinUrl(invite.code, invite.role)}
                  </code>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', width: '100%', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => copyToClipboard(invite.code)}
                    className="btn btn-sm btn-secondary"
                    title="Copy code"
                  >
                    📋 Code
                  </button>
                  <button
                    onClick={() => copyToClipboard(getJoinUrl(invite.code, invite.role))}
                    className="btn btn-sm btn-primary"
                    title="Copy full URL"
                  >
                    🔗 Copy URL
                  </button>
                  {editingInviteId !== invite.id && (
                    <button
                      onClick={() => startEditInvite(invite)}
                      className="btn btn-sm btn-secondary"
                      title="Add more people"
                    >
                      ✏️ Edit
                    </button>
                  )}
                  <button
                    onClick={() => deleteInvite(invite.id)}
                    className="btn btn-sm btn-danger"
                    style={{ marginLeft: 'auto' }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* QR Code Section for Players */}
      <div className="player-qr-section" style={{ marginTop: '2rem' }}>
        <h3>Player Booking QR Code</h3>
        <p style={{ marginBottom: '1rem' }}>Players scan this QR code to book a massage at the event:</p>

        <div className="player-url" style={{ marginBottom: '1.5rem' }}>
          <code style={{ padding: '0.75rem 1rem', background: 'var(--color-bg-tertiary)', borderRadius: '0.5rem', wordBreak: 'break-all' }}>
            {getPlayerUrl()}
          </code>
          <button
            onClick={() => copyToClipboard(getPlayerUrl())}
            className="btn btn-sm btn-primary"
          >
            📋 Copy Link
          </button>
        </div>

        {/* QR Code Generator */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1rem',
          padding: '2rem',
          background: 'var(--color-bg)',
          borderRadius: '1rem',
          border: '2px dashed var(--color-border)'
        }}>
          {qrDataUrl ? (
            <>
              <img
                src={qrDataUrl}
                alt="Player booking QR code"
                style={{
                  width: '250px',
                  height: '250px',
                  borderRadius: '1rem',
                  background: 'white',
                  padding: '1rem'
                }}
              />
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button onClick={downloadQrCode} className="btn btn-large btn-success">
                  ⬇️ Download QR Code
                </button>
                <button onClick={() => setQrDataUrl(null)} className="btn btn-large btn-secondary">
                  🔄 Regenerate
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: '4rem' }}>📱</div>
              <p style={{ color: 'var(--color-text-secondary)', textAlign: 'center' }}>
                Generate a QR code that players can scan to book massages
              </p>
              <button
                onClick={generateQrCode}
                disabled={generatingQr}
                className="btn btn-large btn-primary"
              >
                {generatingQr ? 'Generating...' : '🖨️ Generate QR Code'}
              </button>
            </>
          )}
        </div>

        <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
          Print this QR code and display it at the venue tables for players to scan.
        </p>
      </div>
    </div>
  );
}
